// Календарь пролётов месяца: день со снимком закрашен оттенком прибора.
// Пустой день не красится — «снимков нет» не цвет. Сутки UTC.

import { useMemo } from 'react'

import { formatMonth, useLang, useT } from '../i18n'
import { dayTone, monthGrid, shiftMonth, type CalendarDay, type DayTone } from './datasetCalendar'

export const TONE_CLASS: Record<DayTone, string> = {
  none: 'text-faint',
  optical: 'bg-sky-500/35 text-foam',
  radar: 'bg-violet-500/35 text-foam',
  both: 'bg-gradient-to-br from-sky-500/45 to-violet-500/45 text-foam',
}

interface Props {
  month: string
  days: CalendarDay[]
  selected: string | null
  onPick: (day: string) => void
  onMonth: (month: string) => void
}

export function DatasetMonth({ month, days, selected, onPick, onMonth }: Props) {
  const t = useT()
  const { lang } = useLang()
  const byDate = useMemo(() => new Map(days.map(d => [d.date, d])), [days])
  const grid = useMemo(() => monthGrid(month), [month])
  // Подписи дней недели — из Intl: понедельник 2026-03-02 и шесть следующих.
  const weekdays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(lang, { weekday: 'narrow', timeZone: 'UTC' })
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(Date.UTC(2026, 2, 2 + i))))
  }, [lang])

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <button type="button" onClick={() => onMonth(shiftMonth(month, -1))}
                aria-label={t('imagery.ds.prevMonth')}
                className="rounded-md px-2 text-mist hover:bg-hull/50 hover:text-foam">‹</button>
        <span className="text-[12px] capitalize text-foam">{formatMonth(lang, `${month}-01`)}</span>
        <button type="button" onClick={() => onMonth(shiftMonth(month, 1))}
                aria-label={t('imagery.ds.nextMonth')}
                className="rounded-md px-2 text-mist hover:bg-hull/50 hover:text-foam">›</button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] text-faint">
        {weekdays.map((w, i) => <span key={i}>{w}</span>)}
      </div>
      <div className="mt-0.5 grid grid-cols-7 gap-0.5">
        {grid.flat().map((date, i) => {
          if (!date) return <span key={i} />
          const day = byDate.get(date)
          const tone = dayTone(day)
          const active = tone !== 'none'
          return (
            <button
              key={date}
              type="button"
              disabled={!active}
              onClick={() => onPick(date)}
              title={!day ? undefined : day.records == null
                ? t('imagery.ds.passTip', { optical: day.optical, radar: day.radar })
                : t('imagery.ds.dayTip', {
                  optical: day.optical, radar: day.radar, records: day.records,
                  found: (day.found ?? 0) + (day.identified ?? 0), not_found: day.not_found ?? 0,
                  no_ais: day.no_ais ?? 0,
                })}
              className={`flex h-8 flex-col items-center justify-center rounded-md text-[11px] tabular-nums ${
                TONE_CLASS[tone]} ${selected === date ? 'ring-1 ring-foam' : ''} ${
                active ? 'hover:brightness-125' : 'cursor-default'}`}
            >
              <span>{Number(date.slice(8))}</span>
              {day && (day.records ?? 0) > 0 && (
                <span className="text-[8px] leading-none text-mist">{day.records}</span>
              )}
            </button>
          )
        })}
      </div>
      <div className="mt-1 flex gap-2 text-[10px] text-mist">
        {(['optical', 'radar', 'both'] as const).map(tone => (
          <span key={tone} className="flex items-center gap-1">
            <span className={`inline-block h-2.5 w-2.5 rounded-sm ${TONE_CLASS[tone]}`} />
            {t(`imagery.ds.tone.${tone}`)}
          </span>
        ))}
      </div>
    </div>
  )
}
